import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Edit3 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import ImagePicker from "@/components/ImagePicker";

/**
 * Generic admin CRUD list. Fields config:
 * { key, label, type: 'text'|'textarea'|'number'|'boolean'|'select', options?: string[] }
 */
export default function AdminCrudList({ title, endpoint, fields, defaults = {}, idLabel = "title", formExtras, rowExtras }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get(`/admin/${endpoint}`).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line

  const save = async () => {
    setError("");
    try {
      const payload = { ...editing };
      if (payload.id && items.find((it) => it.id === payload.id)) {
        await api.put(`/admin/${endpoint}/${payload.id}`, payload);
      } else {
        await api.post(`/admin/${endpoint}`, payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || "Error al guardar");
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    await load();
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">{title}</h1>
          <p className="text-sm text-muted-foreground">Total: {items.length}</p>
        </div>
        <button onClick={() => setEditing({ ...defaults })} data-testid={`new-${endpoint}`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nuevo
        </button>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6 bg-background" data-testid={`form-${endpoint}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id && items.find((it) => it.id === editing.id) ? "Editar" : "Nuevo"}</h3>
            <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                {f.type === "image" ? (
                  <ImagePicker label={f.label} value={editing[f.key] || ""} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
                ) : (
                  <>
                    <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
                    {f.type === "textarea" ? (
                  <textarea value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} rows={3} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
                ) : f.type === "boolean" ? (
                  <label className="flex items-center gap-2 h-10">
                    <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="accent-primary" />
                    <span className="text-sm">{editing[f.key] ? "Sí" : "No"}</span>
                  </label>
                ) : f.type === "select" ? (
                  <select value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full h-10 px-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "number" ? (
                  <input type="number" value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: Number(e.target.value) })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
                ) : (
                  <input type="text" value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
                )}
                  </>
                )}
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-destructive mt-4">{error}</p>}
          {formExtras && <div className="mt-5 pt-5 border-t border-border">{formExtras({ editing, setEditing, reload: load })}</div>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} data-testid={`save-${endpoint}`} className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Nombre</th>
              <th className="text-left p-3">Activo</th>
              <th className="text-left p-3">Destacado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-border hover:bg-secondary/30" data-testid={`row-${it.id}`}>
                <td className="p-3 font-semibold">{it[idLabel]}</td>
                <td className="p-3">{it.active ? "Sí" : "No"}</td>
                <td className="p-3">{it.featured ? "Sí" : "—"}</td>
                <td className="p-3 text-right">
                  {rowExtras && rowExtras(it)}
                  <button onClick={() => setEditing({ ...it })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  <button onClick={() => del(it.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Sin elementos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
